import { useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { ShieldCheck } from 'lucide-react';
import { useRoles } from '../../hooks/useRoles';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { RoleResponse } from '../../types/role';
import { RoleList } from './RoleList';
import { RoleDialog } from './RoleDialog';
import { PermissionEditor } from './PermissionEditor';

type DialogState = { mode: 'create' } | { mode: 'edit'; role: RoleResponse } | null;

export default function RolesPage() {
  const { useList, useCreate, useUpdate, useDelete } = useRoles();
  const { data: roles, isLoading, isError, refetch } = useList();

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pendingSelect, setPendingSelect] = useState<RoleResponse | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleResponse | null>(null);

  // Derived from the list so renames and counts stay current after a refetch.
  const selectedRole = roles?.find((r) => r.id === selectedId) ?? null;

  const select = (role: RoleResponse) => {
    setSelectedId(role.id);
    setDirty(false);
  };

  // Switching roles would drop unsaved checkbox changes, so ask first.
  const requestSelect = (role: RoleResponse) => {
    if (role.id === selectedId) return;
    if (dirty) setPendingSelect(role);
    else select(role);
  };

  const handleSaveRole = (name: string, description: string) => {
    if (dialog?.mode === 'edit') {
      updateMutation.mutate(
        { id: dialog.role.id, data: { name, description } },
        { onSuccess: () => setDialog(null) }
      );
    } else {
      createMutation.mutate(
        { name, description },
        { onSuccess: () => setDialog(null) }
      );
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        if (selectedId === deleteTarget.id) {
          setSelectedId(null);
          setDirty(false);
        }
      },
      onSettled: () => setDeleteTarget(null),
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        height: { md: 'calc(100vh - 120px)' },
      }}
    >
      {/* Left panel: role list */}
      <Paper
        component="nav"
        aria-label="Roles"
        sx={{
          width: { xs: '100%', md: 340 },
          flexShrink: 0,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          maxHeight: { xs: 420, md: 'none' },
        }}
      >
        <RoleList
          roles={roles}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => refetch()}
          selectedId={selectedId}
          onSelect={requestSelect}
          onCreate={() => setDialog({ mode: 'create' })}
          onEdit={(role) => setDialog({ mode: 'edit', role })}
          onDelete={setDeleteTarget}
        />
      </Paper>

      {/* Right panel: permissions */}
      <Paper
        component="section"
        aria-label="Role permissions"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: { xs: 520, md: 0 },
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selectedRole ? (
          <PermissionEditor key={selectedRole.id} role={selectedRole} onDirtyChange={setDirty} />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 3,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <ShieldCheck size={40} strokeWidth={1.2} aria-hidden />
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Select a role
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 320 }}>
              Pick a role on the left to see what it can do. Permissions are grouped by module and feature, and you can
              search, filter and change them in bulk.
            </Typography>
          </Box>
        )}
      </Paper>

      {dialog && (
        <RoleDialog
          key={dialog.mode === 'edit' ? dialog.role.id : 'new'}
          initial={dialog.mode === 'edit' ? dialog.role : null}
          loading={createMutation.isPending || updateMutation.isPending}
          onClose={() => setDialog(null)}
          onSave={handleSaveRole}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete role"
        message={
          deleteTarget?.userCount
            ? `"${deleteTarget.name}" is assigned to ${deleteTarget.userCount} user${deleteTarget.userCount !== 1 ? 's' : ''}. Deleting it removes the role from them. This cannot be undone.`
            : `Delete the role "${deleteTarget?.name}"? This cannot be undone.`
        }
        confirmLabel="Delete role"
        severity="danger"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!pendingSelect}
        title="Discard unsaved changes?"
        message={`You changed permissions for "${selectedRole?.name ?? 'this role'}" but haven't saved them. Switching to "${pendingSelect?.name}" will discard those changes.`}
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        severity="warning"
        onConfirm={() => {
          if (pendingSelect) select(pendingSelect);
          setPendingSelect(null);
        }}
        onCancel={() => setPendingSelect(null)}
      />
    </Box>
  );
}
